// Számla-űrlap tiszta segédei (nem-komponens): a form-értékek típusa, a betöltött detail → form-értékek
// leképezés, a form → wire-body építők (create + draft/PUT), a resolver-séma (kliens-oldali elő-ellenőrzés),
// a lokalizált zod-hibatérkép, és a tétel élő-előnézet defenzív számítója. Külön fájl a komponensektől, hogy a
// react-refresh (Fast Refresh) tiszta maradjon (az invoice-form.tsx CSAK komponenseket exportál). A minta a
// features/accounts/form.ts + features/messages/form.ts.
//
// PÉNZ: Decimal-STRING végig (soha number). A kliens ÖSSZEGET NEM küld a wire-be — a body tételenként csak
// qty/unitPrice/vatRate/name/note/quantityUnit; a totálokat + a tétel-összegeket a szerver számolja
// (a @atlas/shared kanonikus fixpontos függvényével). Az élő előnézet UGYANAZT a függvényt hívja, ezért
// a UI és a szerver eredménye bitre azonos.
import {
  type InvoiceCreateBodySchema,
  type InvoiceDetailSchema,
  type InvoiceDraftBodySchema,
  type InvoiceRefsSchema,
} from '@atlas/contracts';
import {
  type InvoiceCurrency,
  type InvoiceDeliveryType,
  type InvoiceIssuerKey,
  type InvoicePaymentMethod,
  type InvoiceQuantityUnit,
  type InvoiceType,
  type InvoiceVatRate,
  INVOICE_CURRENCIES,
  INVOICE_DELIVERY_TYPES,
  INVOICE_ISSUER_KEYS,
  INVOICE_PAYMENT_METHODS,
  INVOICE_QUANTITY_UNITS,
  INVOICE_TYPES,
  INVOICE_VAT_RATES,
  calculateInvoiceItem,
  sumInvoiceTotals,
  type InvoiceItemAmounts,
  type InvoiceTotals,
} from '@atlas/shared';
import { type TFunction } from 'i18next';
import { type z } from 'zod';
import { z as zod } from 'zod';

type InvoiceDetail = z.infer<typeof InvoiceDetailSchema>;

// ── Form-értékek (string-alapú; a select/pill-mezők a kulcsokat hordozzák) ──

/** Egy szerkesztett tétel a form-state-ben (a `id` a diff-kulcs; hiányzik → új sor). */
export interface InvoiceItemFormValue {
  /** Meglévő tétel busz-id-ja (a PUT-diff kulcsa); új sornál undefined. */
  id?: string;
  /**
   * A tétel KATALÓGUS-csomagja (kötelező; üres = még nincs választva). Szabad szöveges tétel nincs: a
   * bevétel-kimutatás a csomag kategóriája szerint sorol.
   */
  servicePlanId: string;
  /** Generált (hozzárendelésből jött) tétel: a csomagja nem cserélhető — a választó zárolt. */
  planLocked: boolean;
  name: string;
  quantity: string;
  quantityUnit: InvoiceQuantityUnit;
  unitPrice: string;
  vatRate: InvoiceVatRate;
  note: string;
}

/** A teljes számla-form (fej + tétel-lista). A `billingEmails` vesszős STRING (a UI parse-ol/joinol). */
export interface InvoiceFormValues {
  /** A cél-ügyfél (create-kor választva; edit-kor zárolt — a detailből). */
  clientId: string;
  scopeKind: 'account' | 'client';
  /** account-scope: egy fiók. */
  accountId: string;
  /** client-scope: fiók-lista (default: minden aktív fiók). */
  accountIds: string[];
  issuerKey: InvoiceIssuerKey;
  type: InvoiceType;
  deliveryType: InvoiceDeliveryType;
  paymentMethod: InvoicePaymentMethod;
  currency: InvoiceCurrency;
  fulfillmentDate: string;
  issueDate: string;
  dueDate: string;
  billingName: string;
  billingTaxNumber: string;
  billingEuTaxNumber: string;
  billingCountryCode: string;
  billingZipCode: string;
  billingCity: string;
  billingAddress: string;
  /** Vesszővel/pontosvesszővel/sortöréssel tagolt e-mail lista (a wire `string[]`-re parse-olja). */
  billingEmails: string;
  note: string;
  items: InvoiceItemFormValue[];
}

/** Egy üres tétel-sor (az „ÁFA-kulcs" a jelenlegi zárolt kulcsot örökli — lásd az invoice-form.tsx add-logikáját). */
export function emptyInvoiceItem(vatRate: InvoiceVatRate = 'VAT_27'): InvoiceItemFormValue {
  return {
    servicePlanId: '',
    planLocked: false,
    name: '',
    quantity: '1',
    quantityUnit: 'piece',
    unitPrice: '0',
    vatRate,
    note: '',
  };
}

/** Egy választható katalógus-csomag (a `GET /invoices/refs` `servicePlans` eleme). */
export type InvoiceServicePlanRef = z.infer<typeof InvoiceRefsSchema>['servicePlans'][number];

/**
 * A katalógus-csomag VÁLASZTÁSAKOR a tétel előtöltése: a számlán megjelenő név, a csomag megjegyzése (csak ha a
 * mező üres — a kézzel írtat nem írjuk felül), az ÁFA-kulcs (ha a sor nincs az ÁFA-lock alatt) és — fix áras
 * csomagnál, a számla devizájában — az egységár. Minden átírható marad: a besorolás a csomagból jön, nem a
 * szövegből, a kedvezmény pedig ár-kérdés.
 */
export function servicePlanPrefill(
  item: Pick<InvoiceItemFormValue, 'note'>,
  plan: InvoiceServicePlanRef,
  currency: InvoiceCurrency,
  vatLocked: boolean,
): Partial<InvoiceItemFormValue> {
  const patch: Partial<InvoiceItemFormValue> = { name: plan.itemName };
  if (plan.itemNote !== null && item.note.trim() === '') patch.note = plan.itemNote;
  const vatRate = plan.vatRate;
  if (
    !vatLocked &&
    vatRate !== null &&
    (INVOICE_VAT_RATES as readonly string[]).includes(vatRate)
  ) {
    patch.vatRate = vatRate as InvoiceVatRate;
  }
  if (plan.unitPrice !== null && plan.unitPrice.currency === currency) {
    patch.unitPrice = plan.unitPrice.netPrice;
  }
  return patch;
}

/** Üres form (create-mód default): HUF, egy üres tétel, ma-kelt dátumok. */
export function emptyInvoiceForm(today: string, dueDefault: string): InvoiceFormValues {
  return {
    clientId: '',
    scopeKind: 'client',
    accountId: '',
    accountIds: [],
    issuerKey: 'none',
    type: 'invoice',
    deliveryType: 'electronic',
    paymentMethod: 'bank_transfer',
    currency: 'HUF',
    fulfillmentDate: today,
    issueDate: today,
    dueDate: dueDefault,
    billingName: '',
    billingTaxNumber: '',
    billingEuTaxNumber: '',
    billingCountryCode: '',
    billingZipCode: '',
    billingCity: '',
    billingAddress: '',
    billingEmails: '',
    note: '',
    items: [emptyInvoiceItem()],
  };
}

/** ISO-date (YYYY-MM-DD) — a helyi „ma" (nem UTC, nincs nap-csúszás). */
export function todayIsoDate(now: Date = new Date()): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** issue-dátum + N nap → ISO-date (a due-mező UI-defaultja; N a profil `defaultInvoicePaymentDeadlineDays`-e vagy 8). */
export function addDaysIso(iso: string, days: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setDate(d.getDate() + days);
  return todayIsoDate(d);
}

// ── E-mail lista parse/join (a vesszős input ↔ string[]) ──

/** Vesszős/pontosvesszős/soremeléses input → trimmelt, üres-mentes e-mail tömb. */
export function parseEmails(raw: string): string[] {
  return raw
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter((s) => s !== '');
}

/** e-mail tömb → vesszős megjelenítés (a form-mezőbe töltéshez). */
export function joinEmails(emails: readonly string[]): string {
  return emails.join(', ');
}

// ── detail → form-értékek ──

const asType = <T extends string>(values: readonly T[], v: string | null, fallback: T): T =>
  values.includes(v as T) ? (v as T) : fallback;

/** A betöltött detail → form-értékek (edit-mód): scope a `accountId`/`accountIds`-ből, enum-fallbackek. */
export function invoiceToFormValues(detail: InvoiceDetail): InvoiceFormValues {
  const scopeKind: 'account' | 'client' = detail.accountId ? 'account' : 'client';
  return {
    clientId: detail.clientId,
    scopeKind,
    accountId: detail.accountId ?? '',
    accountIds: detail.accountIds,
    issuerKey: asType(INVOICE_ISSUER_KEYS, detail.issuerKey, 'none'),
    type: asType(INVOICE_TYPES, detail.type, 'invoice'),
    deliveryType: asType(INVOICE_DELIVERY_TYPES, detail.deliveryType, 'electronic'),
    paymentMethod: asType(INVOICE_PAYMENT_METHODS, detail.paymentMethod, 'bank_transfer'),
    currency: asType(INVOICE_CURRENCIES, detail.currency, 'HUF'),
    fulfillmentDate: detail.fulfillmentDate ?? '',
    issueDate: detail.issueDate ?? '',
    dueDate: detail.dueDate ?? '',
    billingName: detail.billingName ?? '',
    billingTaxNumber: detail.billingTaxNumber ?? '',
    billingEuTaxNumber: detail.billingEuTaxNumber ?? '',
    billingCountryCode: detail.billingCountryCode ?? '',
    billingZipCode: detail.billingZipCode ?? '',
    billingCity: detail.billingCity ?? '',
    billingAddress: detail.billingAddress ?? '',
    billingEmails: joinEmails(detail.billingEmails),
    note: detail.note ?? '',
    items: detail.items.map((it) => ({
      id: it.id,
      // csomag nélküli régi sor → üres: mentés előtt választani kell
      servicePlanId: it.servicePlanId ?? '',
      // a generált tétel csomagja zárolt (a szerver is elutasítja a cserét); csomag nélkül választható
      planLocked: it.servicePlanAssignmentId !== null && it.servicePlanId !== null,
      name: it.name,
      quantity: it.quantity,
      quantityUnit: asType(INVOICE_QUANTITY_UNITS, it.quantityUnit, 'piece'),
      unitPrice: it.unitPrice,
      vatRate: asType(INVOICE_VAT_RATES, it.vatRate, 'VAT_27'),
      note: it.note ?? '',
    })),
  };
}

// ── form → wire-body (a kliens ÖSSZEGET NEM küld) ──

type DraftBody = z.input<typeof InvoiceDraftBodySchema>;
type CreateBody = z.input<typeof InvoiceCreateBodySchema>;

const nullable = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/** A scope discriminated union felépítése a form-ból. */
function buildScope(values: InvoiceFormValues): DraftBody['scope'] {
  return values.scopeKind === 'account'
    ? { kind: 'account', accountId: values.accountId }
    : { kind: 'client', accountIds: values.accountIds };
}

/** A tétel-lista wire-alakja: CSAK a bemeneti mezők (id? + csomag + name/qty/unit/unitPrice/vat/note) — összeg NÉLKÜL. */
function buildItems(values: InvoiceFormValues): DraftBody['items'] {
  return values.items.map((it) => ({
    ...(it.id ? { id: it.id } : {}),
    servicePlanId: it.servicePlanId,
    name: it.name.trim(),
    quantity: it.quantity,
    quantityUnit: it.quantityUnit,
    unitPrice: it.unitPrice,
    vatRate: it.vatRate,
    note: nullable(it.note),
  }));
}

/** A közös draft-body (PUT + a create törzse) — a szerver újra-validál + újraszámol. */
export function invoiceFormToDraftBody(values: InvoiceFormValues): DraftBody {
  return {
    scope: buildScope(values),
    issuerKey: values.issuerKey,
    type: values.type,
    deliveryType: values.deliveryType,
    paymentMethod: values.paymentMethod,
    currency: values.currency,
    fulfillmentDate: values.fulfillmentDate,
    issueDate: values.issueDate,
    dueDate: values.dueDate,
    billingName: values.billingName.trim(),
    billingTaxNumber: nullable(values.billingTaxNumber),
    billingEuTaxNumber: nullable(values.billingEuTaxNumber),
    billingCountryCode: values.billingCountryCode.trim(),
    billingZipCode: values.billingZipCode.trim(),
    billingCity: values.billingCity.trim(),
    billingAddress: values.billingAddress.trim(),
    billingEmails: parseEmails(values.billingEmails),
    note: nullable(values.note),
    items: buildItems(values),
  };
}

/** Create-body: a draft-törzs + a cél-ügyfél (CSAK create-nél). */
export function invoiceFormToCreateBody(values: InvoiceFormValues): CreateBody {
  return { ...invoiceFormToDraftBody(values), clientId: values.clientId };
}

// ── Élő előnézet (defenzív) — a shared kanonikus számítójából ──

/** Nem-negatív Decimal-string, max 2 tizedes (a shared `parseDecimal2` bemeneti kontraktja). */
function isDecimalStr(v: string): boolean {
  return /^\d+(\.\d{1,2})?$/.test(v.trim());
}

/** Egy tétel élő összegei — érvénytelen qty/unitPrice esetén `null` (a preview em-dash-t mutat). */
export function previewItemAmounts(
  currency: InvoiceCurrency,
  item: Pick<InvoiceItemFormValue, 'quantity' | 'unitPrice' | 'vatRate'>,
): InvoiceItemAmounts | null {
  if (!isDecimalStr(item.quantity) || !isDecimalStr(item.unitPrice)) return null;
  return calculateInvoiceItem(currency, item.quantity.trim(), item.unitPrice.trim(), item.vatRate);
}

/** Fej-összegek élőben — csak az érvényes tételekből (a hibás sorok kimaradnak, mint a szerveren nem futna). */
export function previewTotals(
  currency: InvoiceCurrency,
  items: Pick<InvoiceItemFormValue, 'quantity' | 'unitPrice' | 'vatRate'>[],
): InvoiceTotals {
  const amounts = items
    .map((it) => previewItemAmounts(currency, it))
    .filter((a): a is InvoiceItemAmounts => a !== null);
  return sumInvoiceTotals(currency, amounts);
}

// ── Resolver-séma (kliens-oldali elő-ellenőrzés; a szerver a végső fal) ──

// NB: a check-ek NEM kapnak üzenet-stringet — különben a Zod azt használná, és a lokalizáló hibatérkép
// SOHA nem futna le (nyers kulcs látszana). A fordítás a `path`+`code` alapján a `buildInvoiceErrorMap`-ben.
const decimalField = zod.string().regex(/^\d+(\.\d{1,2})?$/);

const itemSchema = zod.object({
  id: zod.string().optional(),
  // a katalógus-csomag kötelező — üres → „válassz szolgáltatást"
  servicePlanId: zod.string().min(1),
  planLocked: zod.boolean(),
  name: zod.string().trim().min(1).max(500),
  quantity: decimalField.refine((v) => Number(v) >= 0.01),
  quantityUnit: zod.enum(INVOICE_QUANTITY_UNITS),
  unitPrice: decimalField,
  vatRate: zod.enum(INVOICE_VAT_RATES),
  note: zod.string().max(1000),
});

/**
 * A form-input validálása (mind string-alapú). A kötelező fej-mezők, a tétel-formátum + a keresztmezős
 * szabályok (scope-fiók megléte, ÁFA-egyöntetűség, EUFAD37→EU-adószám, ≥1 tétel). A path-ok a form-mezőkre
 * mutatnak (RHF setError), a hiba-KÓDOK a `buildInvoiceErrorMap`-ben fordulnak.
 */
export const invoiceFormSchema = zod
  .object({
    clientId: zod.string(),
    scopeKind: zod.enum(['account', 'client']),
    accountId: zod.string(),
    accountIds: zod.array(zod.string()),
    issuerKey: zod.enum(INVOICE_ISSUER_KEYS),
    type: zod.enum(INVOICE_TYPES),
    deliveryType: zod.enum(INVOICE_DELIVERY_TYPES),
    paymentMethod: zod.enum(INVOICE_PAYMENT_METHODS),
    currency: zod.enum(INVOICE_CURRENCIES),
    fulfillmentDate: zod.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    issueDate: zod.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    dueDate: zod.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    billingName: zod.string().trim().min(1).max(255),
    billingTaxNumber: zod.string().max(50),
    billingEuTaxNumber: zod.string().max(50),
    billingCountryCode: zod.string().trim().min(2).max(2),
    billingZipCode: zod.string().trim().min(1).max(20),
    billingCity: zod.string().trim().min(1).max(255),
    billingAddress: zod.string().trim().min(1).max(500),
    billingEmails: zod.string(),
    note: zod.string().max(5000),
    items: zod.array(itemSchema).min(1).max(200),
  })
  .superRefine((values, ctx) => {
    // Scope-fiók megléte
    if (values.scopeKind === 'account' && values.accountId.trim() === '') {
      ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ['accountId'] });
    }
    if (values.scopeKind === 'client' && values.accountIds.length === 0) {
      ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ['accountIds'] });
    }
    // E-mail elemek
    for (const email of parseEmails(values.billingEmails)) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ['billingEmails'] });
        break;
      }
    }
    // ÁFA-egyöntetűség (a UI-lock is véd; ez a defenzív háló) — a sértő sor indexére
    const first = values.items[0]?.vatRate;
    if (first) {
      values.items.forEach((it, i) => {
        if (it.vatRate !== first) {
          ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ['items', i, 'vatRate'] });
        }
      });
    }
    // EUFAD37 → kötelező EU-adószám
    const hasEufad = values.items.some((it) => it.vatRate === 'EUFAD37');
    if (hasEufad && values.billingEuTaxNumber.trim() === '') {
      ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ['billingEuTaxNumber'] });
    }
  });

/**
 * Lokalizált zod-hibatérkép: a custom üzenet-KULCSOKAT (message string) a `validation:invoices.*` rétegre
 * fordítja. Ismeretlen → a zod default üzenete. A szerver 422-terelése ugyanabból a rétegből fordít.
 */
export function buildInvoiceErrorMap(t: TFunction): z.ZodErrorMap {
  const msg = (k: string): { message: string } => ({ message: t(`validation:invoices.${k}`) });
  return (issue, ctx) => {
    const p = issue.path;
    const tooLong = issue.code === zod.ZodIssueCode.too_big;
    // Tétel-szintű mezők: ['items', i, mező]
    if (p[0] === 'items' && typeof p[1] === 'number' && typeof p[2] === 'string') {
      switch (p[2]) {
        case 'servicePlanId':
          return msg('item.servicePlan');
        case 'name':
          return tooLong ? msg('tooLong') : msg('item.name');
        case 'quantity':
          // regex-hiba (invalid_string) → formátum; a min-refine (custom) → mennyiség-minimum
          return issue.code === zod.ZodIssueCode.invalid_string
            ? msg('item.format')
            : msg('item.quantity');
        case 'unitPrice':
          return msg('item.format');
        case 'vatRate':
          return msg('vatMix');
        case 'note':
          return msg('tooLong');
      }
    }
    // A tömb-szintű „legalább 1 tétel" (path: ['items'])
    if (p[0] === 'items') return msg('itemsRequired');
    // Fej-mezők (path[0] = mezőnév)
    const field = typeof p[0] === 'string' ? p[0] : '';
    switch (field) {
      case 'billingName':
        return tooLong ? msg('tooLong') : msg('billingName');
      case 'billingCountryCode':
        return msg('country'); // min(2)/max(2): túl rövid ÉS túl hosszú is „országkód"
      case 'billingZipCode':
        return tooLong ? msg('tooLong') : msg('zip');
      case 'billingCity':
        return tooLong ? msg('tooLong') : msg('city');
      case 'billingAddress':
        return tooLong ? msg('tooLong') : msg('address');
      case 'fulfillmentDate':
      case 'issueDate':
      case 'dueDate':
        return msg('date');
      case 'accountId':
        return msg('accountRequired');
      case 'accountIds':
        return msg('accountsRequired');
      case 'billingEmails':
        return msg('email');
      case 'billingEuTaxNumber':
        return tooLong ? msg('tooLong') : msg('euTaxRequired');
    }
    // Bármely egyéb hossz-túllépés (billingTaxNumber, note) → generikus „túl hosszú".
    if (tooLong) return msg('tooLong');
    return { message: ctx.defaultError };
  };
}
